import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Container, Grid, Divider } from "@material-ui/core";

import FilterCard from "components/_refactor/filter-card";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import LoadingData from "components/Loading/LoadingData";
import TableVitrineVirtual from "./components/table-vitrine-virtual";
import DialogDetailShowcase from "./components/dialog-detail-showcase";
import DialogEditShowcase from "./components/dialog-edit-showcase";
import DialogSendShowcase from "./components/dialog-send-showcase";

import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import useLogs from "hooks/logs";
import { IsMobile900, IsMobile500, IsMobile } from "utils/is-mobile";
import FromToDateField from "components/_refactor/fields/from-to-date";

const STATUS_OPTIONS = [
  { value: "true", name: "Ativa", id: 1 },
  { value: "false", name: "Inativa", id: 2 },
  { value: "", name: "Todas", id: 3 },
];

const INITIAL_FILTERS = {
  initialDate: "",
  finalDate: "",
  status: STATUS_OPTIONS[2],
  structure: {},
};

export default function MyVirtualShowcase() {
  const location = useLocation();
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();

  const isMobile900 = IsMobile900();
  const isMobile550 = IsMobile500();
  const isMobile = IsMobile();
  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [clearFilters, setClearFilters] = useState(false);

  const [fetchStatus, setFetchStatus] = useState("idle");
  const [structureLoading, setStructureLoading] = useState([]);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    totalRegisters: 0,
    currentPage: 1,
    pageSize: 25,
  });

  const [dialogs, setDialogs] = useState({
    detail: false,
    edit: false,
    send: false,
  });
  const [selectedShowcase, setSelectedShowcase] = useState(null);

  const getShowcaseList = useCallback(
    async ({ page, pageSize, idLoja }) => {
      setFetchStatus("loading");

      const params = new URLSearchParams({
        ativo: filters.status?.value ?? "",
        dataVigenciaInicial: filters.initialDate,
        dataVigenciaFinal: filters.finalDate,
        idLoja: idLoja || filters.structure?.idLoja,
        page: page - 1,
        pageSize: pageSize,
      });

      for (let p of params) {
        if (!p[1]) params.delete(p[0]);
      }

      try {
        const response = await Api.get(
          "virtual-showcase-list",
          "showcase",
          `?${params.toString()}`,
          true
        );
        const result = response?.Result;

        setData(result?.ListaVitrines || []);
        setPagination({
          totalRegisters: result?.TotalRegistro || 0,
          currentPage: page,
          pageSize: pageSize,
        });
        setFetchStatus("success");
      } catch (error) {
        console.error(error);
        snackbar.showMessage("Falha ao buscar vitrines. Tente novamente.", {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "159",
          screen: "Vitrine Virtual",
          problem: `Erro em virtual-showcase-list: ${error.message || error}`,
        });
        setFetchStatus("error");
        setData([]);
      }
    },
    [filters, snackbar, setLocalStorageApiProblem]
  );

  const handleSearch = () => {
    getShowcaseList({ page: 1, pageSize: pagination.pageSize });
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setData([]);
    setFetchStatus("idle");
    setClearFilters(true);
    setPagination((prev) => ({ ...prev, totalRegisters: 0, currentPage: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    getShowcaseList({ page, pageSize });
  };

  const openDialog = (type, showcaseData) => {
    setSelectedShowcase(showcaseData);
    setDialogs((prev) => ({ ...prev, [type]: true }));
  };

  const closeDialog = (type, shouldRefresh = false) => {
    setDialogs((prev) => ({ ...prev, [type]: false }));
    setSelectedShowcase(null);
    if (shouldRefresh) {
      getShowcaseList({
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      });
    }
  };

  useEffect(() => {
    const params = UtilsFncs.GetUrlSearchParams(location.search);
    if (params.idLoja) {
      const idLoja = parseInt(params.idLoja, 10);
      setFilters((prev) => ({ ...prev, structure: { idLoja } }));
      getShowcaseList({ page: 1, pageSize: 25, idLoja });
    }
  }, [location.search]);

  const isLoading = fetchStatus === "loading";
  const hasSearched = fetchStatus !== "idle";

  return (
    <>
      <HeaderClientes section="Vitrine Virtual" subsection="Vendas" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={isLoading || structureLoading.length > 0}
          searching={isLoading}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            clearFilters={clearFilters}
            onStructureFieldsChange={(structure) => {
              setFilters((prev) => {
                // Só atualiza se mudou de fato
                if (
                  JSON.stringify(prev.structure) !== JSON.stringify(structure)
                ) {
                  return { ...prev, structure };
                }
                return prev;
              });
            }}
            onStructureFieldsLoad={setStructureLoading}
            disableAll={isLoading}
            hideFields={[
              "channel",
              "representative",
              "economicGroup",
              "storeSingle",
            ]}
          />

          <Grid item xs={12} style={{ paddingRight: "-1rem" }}>
            <Divider style={{ margin: "0.5rem 0 2rem" }} />
          </Grid>

          <Grid item xs={itemXS}>
            <AutoCompleteField
              label="Status da Vitrine"
              onChange={(e, newValue) =>
                setFilters((prev) => ({ ...prev, status: newValue }))
              }
              value={filters.status}
              options={STATUS_OPTIONS}
              disabled={isLoading}
              loading={clearFilters}
              optionLabelKey="name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FromToDateField
              isMobile900={isMobile550}
              label={"Vigência"}
              searching={isLoading}
              initialDate={
                typeof filters.initialDate === "string"
                  ? filters.initialDate
                  : ""
              }
              finalDate={
                typeof filters.finalDate === "string" ? filters.finalDate : ""
              }
              setInitialDate={(value) =>
                setFilters((prev) => ({ ...prev, initialDate: value }))
              }
              setFinalDate={(value) =>
                setFilters((prev) => ({ ...prev, finalDate: value }))
              }
            />
          </Grid>
        </FilterCard>

        <LoadingData
          searching={isLoading}
          onClick={() => setFetchStatus("idle")}
          label="Buscando dados..."
        />

        {hasSearched && !isLoading && (
          <TableVitrineVirtual
            dataTable={data}
            pagination={pagination}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onActionClick={(action, showcase) => {
              setSelectedShowcase(showcase);
              openDialog(action, showcase);
            }}
          />
        )}

        {dialogs.detail && (
          <DialogDetailShowcase
            data={selectedShowcase}
            handleClose={() => closeDialog("detail")}
          />
        )}

        {dialogs.send && (
          <DialogSendShowcase
            data={selectedShowcase}
            handleClose={() => closeDialog("send")}
          />
        )}
        {dialogs.edit && (
          <DialogEditShowcase
            data={selectedShowcase}
            filter={filters.structure}
            handleClose={(action) => closeDialog("edit", action === "edit")}
          />
        )}
      </Container>
    </>
  );
}
